import type { ReactNode } from 'react';

/** True when the signed-in account is a read-only guest. */
export function isGuestViewer(): boolean {
  try {
    const raw = localStorage.getItem('boss_user');
    return raw ? (JSON.parse(raw) as { role?: string }).role === 'viewer' : false;
  } catch {
    return false;
  }
}

/**
 * Guests may open the tab but never see the content: they get a blurred
 * stand-in with a "Confidential" card, and the real page is not mounted, so
 * nothing is fetched. The API refuses these reads for guests as well.
 */
export function GuestConfidential({ label, children }: { label: string; children: ReactNode }) {
  if (!isGuestViewer()) return <>{children}</>;
  return (
    <div style={{ position: 'relative', minHeight: 'calc(100vh - 120px)', overflow: 'hidden', borderRadius: 16 }}>
      <div aria-hidden style={{ filter: 'blur(10px)', opacity: 0.55, padding: 24, display: 'grid', gap: 12, pointerEvents: 'none' }}>
        {Array.from({ length: 9 }, (_, i) => (
          <div key={i} style={{ display: 'flex', gap: 14, alignItems: 'center', padding: 14, borderRadius: 12, background: 'rgba(127,127,127,0.18)' }}>
            <div style={{ width: 40, height: 40, borderRadius: '50%', background: 'rgba(127,127,127,0.45)' }} />
            <div style={{ flex: 1, display: 'grid', gap: 8 }}>
              <div style={{ width: `${30 + ((i * 17) % 40)}%`, height: 12, borderRadius: 6, background: 'rgba(127,127,127,0.5)' }} />
              <div style={{ width: `${55 + ((i * 23) % 35)}%`, height: 10, borderRadius: 6, background: 'rgba(127,127,127,0.3)' }} />
            </div>
          </div>
        ))}
      </div>
      <div style={{ position: 'absolute', inset: 0, display: 'grid', placeItems: 'center', padding: 16 }}>
        <div role="status" style={{ maxWidth: 420, textAlign: 'center', padding: '28px 32px', borderRadius: 16, background: '#111827', color: '#f9fafb', border: '1px solid rgba(255,255,255,0.14)', boxShadow: '0 20px 60px rgba(0,0,0,0.45)' }}>
          <div style={{ fontSize: 30, marginBottom: 6 }}>🔒</div>
          <div style={{ fontSize: 22, fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase' }}>Confidential</div>
          <div style={{ marginTop: 10, fontSize: 14, lineHeight: 1.5, color: '#d1d5db' }}>
            {label} messages are private. Guest accounts can see that this area exists, but not its contents.
          </div>
        </div>
      </div>
    </div>
  );
}
